import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createTimer,
  getAllTimers,
  deleteTimer,
  getAllUsers,
  getSyncLogs
} from '../services/vpsApi';
import './Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-counter.bancheng.tech/api';

function Admin() {
  const [activeTab, setActiveTab] = useState('timers');
  const [timers, setTimers] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTimer, setNewTimer] = useState({
    name: '',
    duration: '',
    userId: '',
    category: '',
    description: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'timers') {
        const data = await getAllTimers();
        setTimers(data);
      } else if (activeTab === 'sync') {
        const data = await getSyncLogs();
        setSyncLogs(data);
      } else if (activeTab === 'users') {
        const data = await getAllUsers();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTimer = async (e) => {
    e.preventDefault();

    try {
      await createTimer({
        name: newTimer.name,
        duration: parseInt(newTimer.duration) || 0,
        userId: newTimer.userId,
        category: newTimer.category,
        description: newTimer.description,
        status: 'pending'
      });

      setNewTimer({
        name: '',
        duration: '',
        userId: '',
        category: '',
        description: ''
      });

      alert('計時器建立成功！');
      loadData();
    } catch (err) {
      alert('建立計時器失敗：' + err.message);
    }
  };

  const handleDeleteTimer = async (id) => {
    if (!window.confirm('確定要刪除這個計時器嗎？')) return;

    try {
      await deleteTimer(id);
      loadData();
    } catch (err) {
      alert('刪除失敗：' + err.message);
    }
  };

  const handleSyncNow = async () => {
    try {
      const response = await fetch(`${API_URL}/webhook/sheets-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manual: true })
      });

      const result = await response.json();

      if (result.success) {
        alert(`同步完成！已同步 ${result.recordsSynced} 筆資料`);
        loadData();
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      alert('同步失敗：' + err.message);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('zh-TW');
  };

  return (
    <div className="admin">
      <header className="admin-header">
        <div className="header-left">
          <h1 className="page-title">後台管理</h1>
          <p className="page-subtitle">管理計時器、同步紀錄與使用者</p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          返回儀表板
        </button>
      </header>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'timers' ? 'active' : ''}`}
          onClick={() => setActiveTab('timers')}
        >
          計時器管理
        </button>
        <button
          className={`tab ${activeTab === 'sync' ? 'active' : ''}`}
          onClick={() => setActiveTab('sync')}
        >
          同步紀錄
        </button>
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          使用者管理
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'timers' && (
          <div className="timers-management">
            <div className="card">
              <h2 className="card-title">新增計時器</h2>
              <form onSubmit={handleCreateTimer} className="timer-form">
                <div className="form-group">
                  <label>名稱 *</label>
                  <input
                    type="text"
                    value={newTimer.name}
                    onChange={(e) => setNewTimer({ ...newTimer, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>時長（秒）</label>
                  <input
                    type="number"
                    value={newTimer.duration}
                    onChange={(e) => setNewTimer({ ...newTimer, duration: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>使用者 ID</label>
                  <input
                    type="text"
                    value={newTimer.userId}
                    onChange={(e) => setNewTimer({ ...newTimer, userId: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>類別</label>
                  <input
                    type="text"
                    value={newTimer.category}
                    onChange={(e) => setNewTimer({ ...newTimer, category: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>描述</label>
                  <textarea
                    value={newTimer.description}
                    onChange={(e) => setNewTimer({ ...newTimer, description: e.target.value })}
                    rows="3"
                  />
                </div>
                <button type="submit" className="btn btn-primary">建立計時器</button>
              </form>
            </div>

            <div className="card">
              <h2 className="card-title">所有計時器</h2>
              {loading ? (
                <div className="loading">載入中...</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>名稱</th>
                        <th>狀態</th>
                        <th>類別</th>
                        <th>使用者</th>
                        <th>時長</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timers.map((timer) => (
                        <tr key={timer.id}>
                          <td>{timer.name}</td>
                          <td>
                            <span className={`status-badge status-${timer.status}`}>
                              {timer.status}
                            </span>
                          </td>
                          <td>{timer.category || '-'}</td>
                          <td>{timer.userId || '-'}</td>
                          <td>{timer.duration ? `${timer.duration}s` : '-'}</td>
                          <td>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteTimer(timer.id)}
                            >
                              刪除
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sync' && (
          <div className="sync-management">
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">同步紀錄</h2>
                <button className="btn btn-primary" onClick={handleSyncNow}>
                  立即同步
                </button>
              </div>
              {loading ? (
                <div className="loading">載入中...</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>時間</th>
                        <th>類型</th>
                        <th>狀態</th>
                        <th>同步筆數</th>
                        <th>錯誤訊息</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncLogs.map((log) => (
                        <tr key={log.id}>
                          <td>{formatTimestamp(log.syncedAt)}</td>
                          <td>{log.syncType}</td>
                          <td>
                            <span className={`status-badge status-${log.status === 'success' ? 'completed' : 'danger'}`}>
                              {log.status}
                            </span>
                          </td>
                          <td>{log.recordsSynced}</td>
                          <td>{log.errorMessage || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-management">
            <div className="card">
              <h2 className="card-title">使用者列表</h2>
              {loading ? (
                <div className="loading">載入中...</div>
              ) : users.length === 0 ? (
                <div className="empty-state">尚無使用者資料</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>名稱</th>
                        <th>Email</th>
                        <th>角色</th>
                        <th>建立時間</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td>{user.id}</td>
                          <td>{user.name}</td>
                          <td>{user.email || '-'}</td>
                          <td>{user.role}</td>
                          <td>{formatTimestamp(user.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
